import React, { Component } from 'react';
import { connect } from 'react-redux';
import { SUCCEEDED as FETCHING_STATUS_SUCCEEDED } from 'constants/status/fetchingStatus';
import { HOTEL as SUMMARY_TYPE_HOTEL } from 'state/checkoutPage/travelSummary/constants/summariesTypes';
import {
  SELECT_ROOMS as STEP_KEY_SELECT_ROOMS,
  SELECT_FLIGHT as STEP_KEY_SELECT_FLIGHT,
  SELECT_FLIGHT_EXTRAS as STEP_KEY_SELECT_FLIGHT_EXTRAS,
  PERSONAL_DATA as STEP_KEY_PERSONAL_DATA,
  PAY_AND_BOOK as STEP_KEY_PAY_AND_BOOK,
} from 'state/checkoutPage/wizard/constants/stepsKeys';
import moveToNewStep from 'state/checkoutPage/wizard/operations/moveToNewStep';
import { setCurrentlyOpenSummaries } from 'state/checkoutPage/travelSummary/actions';
import SummaryButtonComponent from './SummaryButtonComponent';

const buttonMessageDefault = { id: 'next.label', description: 'Next', defaultMessage: 'Next' };
const buttonMessageBackToSelectRooms = {
  id: 'changeRoom.label',
  description: 'Change room',
  defaultMessage: 'Change room',
};
const buttonMessageBackToSelectFlight = {
  id: 'changeFlight.label',
  description: 'Change flight',
  defaultMessage: 'Change flight',
};
const buttonMessageByStep = { id: 'checkoutWizard.stepLabel', description: 'Continue', defaultMessage: 'Continue' };

class SummaryButton extends Component {
  constructor(props) {
    super(props);

    this.state = {
      nextStepId: null,
      buttonMessage: buttonMessageDefault,
    };

    this.handleOnClick = this.handleOnClick.bind(this);
  }

  componentDidUpdate() {
    const { buttonMessage: _buttonMessage } = this.state;
    const { useMessageByStep, checkoutWizard } = this.props;
    const { currentStep, steps } = checkoutWizard;

    if (steps.length === 0) {
      return;
    }

    const nextHotelId = this.getNextHotelId();
    const nextStepId = this.getNextStepId();
    let buttonMessage = this.getNextButtonMessage(nextStepId);

    if (useMessageByStep && nextHotelId === null) {
      const stepId = nextStepId !== null ? nextStepId : steps[currentStep + 1].id;
      buttonMessage = {
        id: `${buttonMessageByStep.id}.${stepId}`,
        description: nextStepId,
        defaultMessage: nextStepId,
      };
    }

    if (_buttonMessage.id === buttonMessage.id) {
      return;
    }

    this.setState({
      nextStepId,
      buttonMessage,
    });
  }

  handleOnClick() {
    const { moveToNewStep, setCurrentlyOpenSummaries } = this.props;
    const { nextStepId } = this.state;
    const nextHotelId = this.getNextHotelId();

    if (nextHotelId) {
      setCurrentlyOpenSummaries([{ summaryType: SUMMARY_TYPE_HOTEL, id: nextHotelId, exclusive: true }]);
    } else {
      moveToNewStep(nextStepId);
    }
  }

  getNextHotelId() {
    const { packagesSearch, checkoutWizard, currentlyOpenSummaries } = this.props;
    const { currentStep, steps } = checkoutWizard;
    const isMultiHotels = packagesSearch.value && packagesSearch.value.hotels && packagesSearch.value.hotels.length > 1;

    if (!isMultiHotels) {
      return null;
    }

    const isSelectRoomsStep = steps[currentStep] !== undefined && steps[currentStep].id === STEP_KEY_SELECT_ROOMS;

    if (!isSelectRoomsStep) {
      return null;
    }

    const currentlyOpenHotel = currentlyOpenSummaries.find(
      (summaryKey) => summaryKey.summaryType === SUMMARY_TYPE_HOTEL
    );

    if (currentlyOpenHotel === undefined) {
      // If all travel summaries are closed, the rooms for the first hotel are displayed by default.
      // That is why we return the id of the next hotel.
      return packagesSearch.value.hotels[1].id;
    }

    const currentHotelIndex = packagesSearch.value.hotels.findIndex((hotel) => hotel.id === currentlyOpenHotel.id);
    const nextHotel = packagesSearch.value.hotels[currentHotelIndex + 1];

    if (nextHotel) {
      return nextHotel.id;
    }

    return null;
  }

  getNextStepId() {
    const { checkoutWizard, flightsDetailsValid, bookingVerify } = this.props;
    const { currentStep, steps } = checkoutWizard;
    const isSelectFlightExtrasStep =
      steps[currentStep] !== undefined && steps[currentStep].id === STEP_KEY_SELECT_FLIGHT_EXTRAS;
    const isPersonalDataStep = steps[currentStep] !== undefined && steps[currentStep].id === STEP_KEY_PERSONAL_DATA;
    const roomIsNotAvailable = !bookingVerify.fetching && bookingVerify.value && bookingVerify.value.valid === false;

    if (isSelectFlightExtrasStep && flightsDetailsValid === false) {
      return STEP_KEY_SELECT_FLIGHT;
    }

    if (isPersonalDataStep && roomIsNotAvailable) {
      return STEP_KEY_SELECT_ROOMS;
    }

    return null;
  }

  getNextButtonMessage(nextStepId) {
    if (nextStepId === STEP_KEY_SELECT_FLIGHT) {
      return buttonMessageBackToSelectFlight;
    }

    if (nextStepId === STEP_KEY_SELECT_ROOMS) {
      return buttonMessageBackToSelectRooms;
    }

    return buttonMessageDefault;
  }

  render() {
    const { packagesSearch, checkoutWizard } = this.props;
    const hotelsRooms =
      packagesSearch.value &&
      packagesSearch.value.hotels &&
      packagesSearch.value.hotels.map((hotel) => hotel.rooms).flat();
    const dataLoaded = packagesSearch.fetching === FETCHING_STATUS_SUCCEEDED && hotelsRooms && hotelsRooms.length > 0;
    const { steps, currentStep } = checkoutWizard;
    const hideComponent =
      !dataLoaded ||
      steps[currentStep] === undefined ||
      (steps[currentStep] && steps[currentStep].id === STEP_KEY_PAY_AND_BOOK);

    if (hideComponent) {
      return null;
    }

    const { simple, styles, airlineWhitelabel, isDataFetching, flightsDetailsReset } = this.props;
    const { buttonMessage } = this.state;

    if (isDataFetching || flightsDetailsReset) {
      return null;
    }

    return (
      <SummaryButtonComponent
        simple={simple}
        styles={styles}
        buttonMessage={buttonMessage}
        onClick={this.handleOnClick}
        airlineWhitelabel={airlineWhitelabel}
      />
    );
  }
}

function mapStateToProps(state) {
  return {
    packagesSearch: state.checkoutPagePackagesSearch,
    checkoutWizard: state.checkoutPageWizard,
    currentlyOpenSummaries: state.checkoutPageTravelSummary.currentlyOpenSummaries,
    bookingVerify: state.checkoutPageBooking.bookingVerify,
    flightsDetailsValid: state.checkoutPageFlightsDetails.validity.valid,
    flightsDetailsReset: state.checkoutPageFlightsDetails.validity.reset,
    airlineWhitelabel: state.settings.value.airlineWhitelabel,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    moveToNewStep(stepId) {
      dispatch(moveToNewStep(stepId));
    },
    setCurrentlyOpenSummaries(currentlyOpenSummaries) {
      dispatch(setCurrentlyOpenSummaries(currentlyOpenSummaries));
    },
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(SummaryButton);
